import type {InvoiceFilters, InvoiceItem, InvoiceMonth} from './types';

import {DownloadOutlined} from '@ant-design/icons';
import {Button, Loading, Select} from '@ds/components';
import {PageLayout} from '@ds/layout';
import Empty from 'common/empty/Empty';
import {useURLFilter} from 'hooks/useURLFilter';
import moment from 'moment-jalaali';
import {useCallback, useEffect, useMemo} from 'react';
import {useAppDispatch, useAppSelector} from 'store';
import {getInvoice, getInvoiceReport, getInvoiceYears} from 'store/slices/invoice/thunk';

import InvoiceCard from './components/InvoiceCard/InvoiceCard';
import MonthSelector from './components/MonthSelector/MonthSelector';

const MONTHS: InvoiceMonth[] = [
  {title: 'فروردین', value: 1},
  {title: 'اردیبهشت', value: 2},
  {title: 'خرداد', value: 3},
  {title: 'تیر', value: 4},
  {title: 'مرداد', value: 5},
  {title: 'شهریور', value: 6},
  {title: 'مهر', value: 7},
  {title: 'آبان', value: 8},
  {title: 'آذر', value: 9},
  {title: 'دی', value: 10},
  {title: 'بهمن', value: 11},
  {title: 'اسفند', value: 12}
];

const Invoice: React.FC = () => {
  const vendorId = JSON.parse(localStorage.getItem('selectedVendor') ?? 'null')?.id;
  const dispatch = useAppDispatch();
  const defaultFilters = useMemo<InvoiceFilters>(() => ({}), []);
  const [filters, setFilters] = useURLFilter<InvoiceFilters>('filters', defaultFilters);
  const {invoiceYears, invoices, loading} = useAppSelector((state) => state.invoice);
  const year = filters.year ?? moment().jYear();
  const month = filters.month ?? moment().jMonth() + 1;

  const updateFilters = useCallback(
    (nextFilters: Partial<InvoiceFilters>) => {
      setFilters({year, month, ...nextFilters});
    },
    [month, setFilters, year]
  );

  useEffect(() => {
    if (!filters.year || !filters.month) {
      setFilters({year, month});
    }
  }, [filters.month, filters.year, month, setFilters, year]);

  useEffect(() => {
    void dispatch(getInvoiceYears({vendor_id: vendorId}));
  }, [dispatch, vendorId]);

  useEffect(() => {
    void dispatch(
      getInvoice({
        vendor_id: vendorId,
        date: `${year}-${month}-01`
      })
    );
  }, [dispatch, month, vendorId, year]);

  const handleDownloadReport = () => {
    void dispatch(
      getInvoiceReport({
        vendor_id: vendorId,
        date: `${year}-${month}-01`
      })
    );
  };

  const invoiceItems = Array.isArray(invoices) ? (invoices as InvoiceItem[]) : [];
  const availableYears = Array.isArray(invoiceYears?.years) ? invoiceYears.years : [];

  return (
    <PageLayout
      header={
        <div className="flex flex-col border-b border-gray-03">
          <div className="flex min-h-12.5 items-center justify-between gap-1 px-4 py-2">
            <Select
              className="max-w-28"
              value={year}
              placeholder="انتخاب سال"
              options={availableYears.map((invoiceYear) => ({
                label: `سال ${invoiceYear}`,
                value: invoiceYear
              }))}
              onChange={(value) => updateFilters({year: Number(value)})}
            />
            <Button size="sm" onClick={handleDownloadReport} startIcon={<DownloadOutlined />}>
              <span className="whitespace-nowrap">دانلود اکسل</span>
            </Button>
          </div>
          <MonthSelector
            items={MONTHS}
            selectedMonth={month}
            onSelect={(selectedMonth) => updateFilters({month: selectedMonth})}
          />
        </div>
      }
    >
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loading size="medium" mId="1" />
        </div>
      ) : invoiceItems.length > 0 && availableYears.length > 0 ? (
        <div className="flex flex-col gap-4 p-4">
          {invoiceItems.map((invoice) => (
            <InvoiceCard key={invoice.id} invoice={invoice} />
          ))}
        </div>
      ) : (
        <Empty className="flex-1 gap-2.5">
          <span className="text-body-sm text-gray-08">هیچ صورتحسابی یافت نشد</span>
        </Empty>
      )}
    </PageLayout>
  );
};

export default Invoice;
